import { z } from 'zod';

import { DOCS_BASE_URL } from '@/constants/docs';

const runtimeConfigSchema = z.object({
  KNOWLEDGE_HUB_URL: z.literal(DOCS_BASE_URL),
  SERVICE_ENVIRONMENT: z.string(),
  CLIENT_TOKEN_ENCRYPTION_KEY: z.string(),
  SEGMENT_WRITE_KEY: z.string(),
  GOOGLE_MAPS_PUBLIC_KEY: z.string(),
  SUPPORT_EMAIL: z.literal('support@gominerva.com'),
  release: z.object({
    imageTag: z.string().min(1),
    sequence: z.number().int().nonnegative(),
  }),
  firebase: z.object({
    apiKey: z.string(),
    authDomain: z.string(),
    databaseURL: z.string().url(),
    projectId: z.string(),
    storageBucket: z.string(),
    messagingSenderId: z.string(),
    appId: z.string(),
    measurementId: z.string(),
  }),
  datadog: z.object({
    applicationId: z.string().optional(),
    clientToken: z.string().optional(),
    env: z
      .literal('prod')
      .or(z.literal('stg'))
      .or(z.literal('qa'))
      .or(z.literal('dev'))
      .optional(),
    version: z.string().optional(),
    service: z.literal('user-dashboard'),
    site: z.literal('us3.datadoghq.com'),
    proxy: z.literal('https://ud-datadog-services.gominerva.com'),
  }),
});

export type Config = z.infer<typeof runtimeConfigSchema>;

// Preserve `undefined` for optional integrations so legacy feature checks keep
// working while the migration removes compatibility layers around them.
function getOptionalEnv(value: string | undefined) {
  return value && value.length > 0 ? value : undefined;
}

export const config = runtimeConfigSchema.parse({
  KNOWLEDGE_HUB_URL: DOCS_BASE_URL,
  SERVICE_ENVIRONMENT: process.env.NEXT_PUBLIC_SERVICE_ENVIRONMENT,
  CLIENT_TOKEN_ENCRYPTION_KEY:
    process.env.NEXT_PUBLIC_CLIENT_TOKEN_ENCRYPTION_KEY,
  SEGMENT_WRITE_KEY: process.env.NEXT_PUBLIC_SEGMENT_WRITE_KEY,
  GOOGLE_MAPS_PUBLIC_KEY: process.env.NEXT_PUBLIC_GOOGLE_MAPS_PUBLIC_KEY,
  SUPPORT_EMAIL: 'support@gominerva.com',
  release: {
    imageTag:
      getOptionalEnv(process.env.NEXT_PUBLIC_RELEASE_IMAGE_TAG) ?? 'local',
    sequence: Number(process.env.NEXT_PUBLIC_RELEASE_SEQUENCE ?? 0),
  },
  firebase: {
    apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
    authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
    databaseURL: process.env.NEXT_PUBLIC_FIREBASE_DATABASE_URL,
    projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
    storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
    measurementId: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID,
  },
  datadog: {
    applicationId: getOptionalEnv(process.env.NEXT_PUBLIC_DATADOG_APP_ID),
    clientToken: getOptionalEnv(process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN),
    env: getOptionalEnv(
      process.env.NEXT_PUBLIC_DATADOG_ENV
    ) as Config['datadog']['env'],
    version: getOptionalEnv(process.env.NEXT_PUBLIC_DATADOG_APP_VERSION),
    service: 'user-dashboard',
    site: 'us3.datadoghq.com',
    proxy: 'https://ud-datadog-services.gominerva.com',
  },
});
